import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
const TOOL_NAMES = {
  customers: "Customers", "customer-terms": "Customer Terms Update",
  "custom-suppliers": "Custom Suppliers", "decorator-matrix": "Decoration Matrix",
  "contact-repair": "Contact Repair", artwork: "Artwork",
};
const fmtDate = value => value ? new Date(value).toLocaleString() : "—";
const headers = () => ({ headers: { accessToken: localStorage.getItem("accessToken") } });
const historyUrl = id => SERVER + "/api/instances/" + encodeURIComponent(id) + "/tools/import/history";
// Date-only inputs represent the administrator\'s complete local day.
const dateParams = (from, to) => ({
  from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
  to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
});
export default function ImportHistoryDialog({ open, onClose, instanceUniqueId }) {
  const [toolKey, setToolKey] = useState("");
  const [status, setStatus] = useState("");
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [selected, setSelected] = useState(null);
  const [detailPage, setDetailPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requestIdRef = useRef(0);
  useEffect(() => {
    requestIdRef.current += 1;
    setSelected(null);
    setDetail(null);
    setData({ rows: [], total: 0 });
    setPage(1);
  }, [instanceUniqueId]);
  const load = useCallback(async () => {
    if (!open || !instanceUniqueId) return;
    const requestId = ++requestIdRef.current;
    setBusy(true); setError("");
    try {
      const response = await axios.get(historyUrl(instanceUniqueId), {
        ...headers(), params: { page, limit: 25, toolKey, status, userId, ...dateParams(from, to) },
      });
      if (requestId === requestIdRef.current) setData(response.data);
    } catch (_) { if (requestId === requestIdRef.current) setError("Unable to load import history."); }
    finally { if (requestId === requestIdRef.current) setBusy(false); }
  }, [open, instanceUniqueId, page, toolKey, status, userId, from, to]);
  useEffect(() => {
    load();
    return () => { requestIdRef.current += 1; };
  }, [load]);
  useEffect(() => {
    if (!selected || !open) return undefined;
    let live = true;
    axios.get(historyUrl(instanceUniqueId) + "/" + encodeURIComponent(selected.id),
      { ...headers(), params: { page: detailPage, limit: 50 } })
      .then(response => { if (live) setDetail(response.data); })
      .catch(() => { if (live) setError("Unable to load import results."); });
    return () => { live = false; };
  }, [selected, detailPage, open, instanceUniqueId]);
  const exportCsv = async () => {
    if (!selected) return;
    try {
      const response = await axios.get(historyUrl(instanceUniqueId) + "/" +
        encodeURIComponent(selected.id) + ".csv", { ...headers(), responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = "elite-core-import-" + selected.id + ".csv";
      anchor.click(); URL.revokeObjectURL(url);
    } catch (_) { setError("CSV export failed."); }
  };
  const exportActivity = async () => {
    try {
      const response = await axios.get(historyUrl(instanceUniqueId) + "/export.csv", {
        ...headers(), params: { toolKey, status, userId, ...dateParams(from, to) }, responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = "elite-core-import-activity.csv";
      anchor.click(); URL.revokeObjectURL(url);
    } catch (_) { setError("Import activity export failed."); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Import History</Typography>
          <Typography variant="body2" color="text.secondary">Past imports and recorded results for this company.</Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField select size="small" label="Import tool" value={toolKey}
              onChange={event => { setPage(1); setToolKey(event.target.value); }} sx={{ minWidth: 190 }}>
              <MenuItem value="">All tools</MenuItem>
              {Object.entries(TOOL_NAMES).map(([key, name]) => <MenuItem key={key} value={key}>{name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Status" value={status}
              onChange={event => { setPage(1); setStatus(event.target.value); }} sx={{ minWidth: 160 }}>
              <MenuItem value="">All statuses</MenuItem>
              {["running", "completed", "partial", "failed"].map(value =>
                <MenuItem value={value} key={value}>{value}</MenuItem>)}
            </TextField>
            <TextField size="small" label="User ID" value={userId}
              onChange={event => { setPage(1); setUserId(event.target.value.replace(/\D/g, "")); }}
              sx={{ width: 110 }} />
            <TextField size="small" label="From" type="date" value={from}
              InputLabelProps={{ shrink: true }} onChange={event => { setPage(1); setFrom(event.target.value); }} />
            <TextField size="small" label="To" type="date" value={to}
              InputLabelProps={{ shrink: true }} onChange={event => { setPage(1); setTo(event.target.value); }} />
            <Button onClick={load} disabled={busy}>Refresh</Button>
            <Button startIcon={<DownloadOutlinedIcon />} onClick={exportActivity}>Export activity</Button>
          </Stack>
          {busy && <CircularProgress size={20} />}
          <Paper variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead><TableRow>
                {["Date", "Tool", "Imported by", "Source", "Status", "Created", "Updated", "Skipped", "Failed", ""].map(h =>
                  <TableCell key={h}>{h}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>{(data.rows || []).map(run => <TableRow hover key={run.id}>
                <TableCell>{fmtDate(run.startedAt)}</TableCell>
                <TableCell>{TOOL_NAMES[run.toolKey] || run.toolKey}</TableCell>
                <TableCell>{run.performedByName}</TableCell>
                <TableCell>{run.sourceName || "—"}</TableCell>
                <TableCell><Chip size="small" label={run.status} color={run.status === "failed" ? "error" : run.status === "partial" ? "warning" : run.status === "completed" ? "success" : "default"} variant="outlined" /></TableCell>
                <TableCell>{run.createdCount}</TableCell>
                <TableCell>{run.updatedCount}</TableCell>
                <TableCell>{run.skippedCount}</TableCell>
                <TableCell>{run.failedCount}</TableCell>
                <TableCell><Button size="small" onClick={() => { setDetail(null); setDetailPage(1); setSelected(run); }}>Results</Button></TableCell>
              </TableRow>)}</TableBody>
            </Table>
            {!busy && !data.rows?.length && <Typography sx={{ p: 2 }} color="text.secondary">No imports found for these filters.</Typography>}
          </Paper>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
            <Typography variant="caption">{data.total || 0} imports</Typography>
            <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Typography variant="body2">Page {page}</Typography>
            <Button disabled={page * 25 >= (data.total || 0)} onClick={() => setPage(page + 1)}>Next</Button>
          </Stack>
          {selected && <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography fontWeight={700}>Import results — {TOOL_NAMES[selected.toolKey]}</Typography>
                <Typography variant="caption" color="text.secondary">Run {selected.id}</Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button startIcon={<DownloadOutlinedIcon />} onClick={exportCsv}>Export CSV</Button>
                <Button onClick={() => { setSelected(null); setDetail(null); }}>Close results</Button>
              </Stack>
            </Stack>
            {selected.errorMessage && <Alert severity="warning" sx={{ mb: 2 }}>{selected.errorMessage}</Alert>}
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead><TableRow>{["Record", "ID", "Action", "Status", "Details"].map(x => <TableCell key={x}>{x}</TableCell>)}</TableRow></TableHead>
                <TableBody>{(detail?.results || []).map(row => <TableRow key={row.id}>
                  <TableCell>{row.recordName || row.recordType || "—"}</TableCell>
                  <TableCell>{row.recordId || "—"}</TableCell>
                  <TableCell>{row.action}</TableCell><TableCell>{row.status}</TableCell>
                  <TableCell>{row.message || "—"}</TableCell>
                </TableRow>)}</TableBody>
              </Table>
            </Box>
            {!detail?.results?.length && <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>No individual result rows recorded. Summary information remains above.</Typography>}
            <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
              <Button disabled={detailPage <= 1} onClick={() => setDetailPage(detailPage - 1)}>Previous</Button>
              <Typography variant="body2" sx={{ alignSelf: "center" }}>Page {detailPage}</Typography>
              <Button disabled={detailPage * 50 >= (detail?.total || 0)} onClick={() => setDetailPage(detailPage + 1)}>Next</Button>
            </Stack>
          </Paper>}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
